import { isNullish } from "@apollo/client/cache/inmemory/helpers";
import { captureException } from "@sentry/react";
import { Project, usePageProjectQuery } from "graphql/_Types";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";

import { IdeasQueryState, NullishDataError } from "types/IdeasQueryState";

export type PageProjectData = {
  project: NonNullable<
    NonNullable<ReturnType<typeof usePageProjectQuery>["data"]>["project"]
  >;
};

export const usePageProjectData = ({
  projectKey,
}: {
  projectKey: Project["key"];
}): IdeasQueryState<PageProjectData> => {
  const currentTenant = useTenantContext((s) => s.currentTenant);
  const { loading, error, data } = usePageProjectQuery({
    variables: {
      projectKey,
      tenantId: currentTenant.id,
    },
    fetchPolicy: "cache-and-network",
    onError: (err) => captureException(err),
  });

  if (loading && data === undefined) {
    return { loading: true, error: undefined, data: undefined };
  }

  if (error) {
    return { loading: false, error, data: undefined };
  }

  const project = data?.project;

  if (isNullish(project)) {
    return { loading: false, error: new NullishDataError(), data: undefined };
  }

  return {
    loading: false,
    error: undefined,
    data: {
      project,
    },
  };
};
